import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  MobileStepper,
  Paper,
  Chip,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Dashboard as GridIcon,
  Shuffle as ShuffleIcon,
  EmojiEvents as TrophyIcon,
  Payments as PayoutIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { hasSeenEducationModal, markEducationModalSeen, resetEducationModal } from '../../lib/educationModals';

interface OnboardingStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  visual: React.ReactNode;
}

const MiniGridVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
    <Box>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: 24 }} />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            fontSize: 10,
            fontWeight: 'bold',
            py: 0.5,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            minWidth: 90,
          }}
        >
          HOME
        </Box>
      </Box>
      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            width: 24,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'error.main',
            color: 'white',
            fontSize: 9,
            fontWeight: 'bold',
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          }}
        >
          AWAY
        </Box>
        <Box>
          {[0, 1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ display: 'flex' }}>
              {[0, 1, 2, 3, 4].map((j) => {
                const colors = ['#e3f2fd', '#fff3e0', '#e8f5e9', '#fce4ec', '#f3e5f5'];
                return (
                  <Box
                    key={j}
                    sx={{
                      width: 18,
                      height: 18,
                      bgcolor: colors[(i + j) % 5],
                      border: 1,
                      borderColor: 'grey.300',
                    }}
                  />
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

const NumbersVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 0.5 }}>
        Before
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[0, 1, 2, 3, 4].map((n) => (
          <Box
            key={n}
            sx={{
              width: 20,
              height: 20,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 0.5,
            }}
          >
            {n}
          </Box>
        ))}
      </Box>
    </Box>
    <ShuffleIcon sx={{ alignSelf: 'center', mt: 2, color: 'primary.main' }} />
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 0.5 }}>
        After
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[3, 0, 4, 1, 2].map((n) => (
          <Box
            key={n}
            sx={{
              width: 20,
              height: 20,
              bgcolor: 'primary.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 0.5,
              border: 1,
              borderColor: 'primary.main',
            }}
          >
            {n}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const WinnerVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', mb: 1 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">Home Score</Typography>
        <Typography variant="h6">17</Typography>
        <Chip label="Last digit: 7" size="small" color="primary" />
      </Box>
      <Typography variant="h6" color="text.secondary">-</Typography>
      <Box>
        <Typography variant="caption" color="text.secondary">Away Score</Typography>
        <Typography variant="h6">24</Typography>
        <Chip label="Last digit: 4" size="small" color="error" />
      </Box>
    </Box>
    <Typography variant="body2" color="text.secondary">
      Row 4 + Column 7 = Winner!
    </Typography>
  </Box>
);

const PayoutVisual: React.FC = () => {
  // Example: $10/square * 100 squares = $1000 pot
  const payouts = [
    { quarter: 'Q1', amount: 200 },
    { quarter: 'Q2', amount: 200 },
    { quarter: 'Q3', amount: 200 },
    { quarter: 'Q4', amount: 400 },
  ];
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
      {payouts.map((p, i) => (
        <Paper
          key={p.quarter}
          elevation={0}
          sx={{
            p: 1,
            textAlign: 'center',
            bgcolor: i === 3 ? 'success.100' : 'grey.100',
            border: 1,
            borderColor: i === 3 ? 'success.main' : 'grey.300',
            minWidth: 55,
          }}
        >
          <Typography variant="caption" fontWeight="bold">{p.quarter}</Typography>
          <Typography variant="body2" fontWeight={i === 3 ? 'bold' : 'normal'}>${p.amount}</Typography>
        </Paper>
      ))}
    </Box>
  );
};

const steps: OnboardingStep[] = [
  {
    title: 'Welcome to Squares!',
    description: 'A squares pool is a 10x10 grid where each square represents a possible game score. Pick your squares and watch the action!',
    icon: <GridIcon sx={{ fontSize: 40 }} />,
    visual: <MiniGridVisual />,
  },
  {
    title: 'Random Numbers',
    description: 'After everyone claims their squares, the numbers 0-9 are randomly assigned to rows and columns. This keeps the game fair!',
    icon: <ShuffleIcon sx={{ fontSize: 40 }} />,
    visual: <NumbersVisual />,
  },
  {
    title: 'Finding Winners',
    description: 'At the end of each quarter, the last digit of each team\'s score determines the winning square. Simple as that!',
    icon: <TrophyIcon sx={{ fontSize: 40 }} />,
    visual: <WinnerVisual />,
  },
  {
    title: 'Multiple Payouts',
    description: 'Win money after each quarter! The final quarter usually has the biggest payout, but every quarter is a chance to win.',
    icon: <PayoutIcon sx={{ fontSize: 40 }} />,
    visual: <PayoutVisual />,
  },
];

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = () => {
    markEducationModalSeen('onboarding');
    setActiveStep(0);
    onClose();
  };

  const handleSkip = () => {
    markEducationModalSeen('onboarding');
    setActiveStep(0);
    onClose();
  };

  if (!open) return null;

  const currentStep = steps[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' },
      }}
    >
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 3,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 1 }}>{currentStep.icon}</Box>
        <Typography variant="h5" fontWeight="bold">
          {currentStep.title}
        </Typography>
      </Box>

      <DialogContent sx={{ py: 4, px: 3 }}>
        <Box sx={{ mb: 3, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {currentStep.visual}
        </Box>
        <Typography variant="body1" textAlign="center" color="text.secondary">
          {currentStep.description}
        </Typography>
      </DialogContent>

      <Box sx={{ px: 3, pb: 2 }}>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{ bgcolor: 'transparent', justifyContent: 'center', mb: 2 }}
          backButton={<Box />}
          nextButton={<Box />}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            size="small"
            onClick={handleSkip}
            sx={{ color: 'text.secondary' }}
            data-testid="education-modal-skip"
          >
            Skip
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<KeyboardArrowLeft />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <KeyboardArrowRight />}
          >
            {activeStep === steps.length - 1 ? 'Get Started' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

// Helper to check if user has seen onboarding
export const hasSeenOnboarding = (): boolean => {
  return hasSeenEducationModal('onboarding');
};

// Helper to reset onboarding (for testing)
export const resetOnboarding = (): void => {
  resetEducationModal('onboarding');
};

export default OnboardingModal;
